import { Stack, TableCell, TableRow, Typography } from '@mui/material';
import { Label } from '@/components';
import { TableRowLink } from '@/components/table';
import { fDateAbbrevMonth } from '@/utils/formatTime';
import { LabelColors } from '@/types';
import { referralsPath } from '../routes/paths';
import { Referral } from '../types';
import {
  getReferralFilterValue,
  getReferralInviter,
  getReferralInviterDisplay,
  getReferralInviterId,
  getReferralInvitee,
  getReferralNeutralStates,
  getReferralProgress,
  getReferralStatus,
  getReferralStatusLabelColor,
  getRewardedLabelColor,
} from '../utils';
import ReferralProgressIndicator from './ReferralProgressIndicator';
import ReferralUserCell from './ReferralUserCell';

type ReferralTableRowProps = {
  row: Referral;
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : fDateAbbrevMonth(value);
};

export default function ReferralTableRow({ row }: ReferralTableRowProps) {
  const inviter = getReferralInviter(row);
  const invitee = getReferralInvitee(row);
  const progress = getReferralProgress(row);
  const neutralStates = getReferralNeutralStates(row);
  const filterValue = getReferralFilterValue(row);

  return (
    <TableRow hover sx={{ verticalAlign: 'top' }}>
      <TableCell>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <ReferralUserCell
            user={inviter}
            userId={getReferralInviterId(row)}
            fallback={getReferralInviterDisplay(row)}
          />
          <TableRowLink
            linkText={`Referral #${row.id}`}
            linkTo={referralsPath('detail', {
              referralId: String(row.id),
            })}
            sx={{ px: 0, py: 0, width: 'fit-content', fontSize: 12 }}
          />
        </Stack>
      </TableCell>
      <TableCell>
        <ReferralUserCell
          user={invitee}
          email={row.invitee_email}
          fallback="N/A"
          neutralText={!invitee ? 'Invitee has not signed up' : undefined}
        />
      </TableCell>
      <TableCell>
        <Stack spacing={0.75} alignItems="flex-start">
          <Label
            color={getReferralStatusLabelColor(row) as LabelColors}
            sx={{ textTransform: 'capitalize' }}
          >
            {getReferralStatus(row)}
          </Label>
          {filterValue === 'ready_rewarded' && !progress.isRewarded && (
            <Typography variant="caption" color="text.secondary">
              Ready for backend reward
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <ReferralProgressIndicator referral={row} compact />
        {neutralStates.includes(
          `0 / ${progress.required} sessions complete`
        ) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75 }}
          >
            0 / {progress.required} sessions complete
          </Typography>
        )}
      </TableCell>
      <TableCell>{progress.remaining}</TableCell>
      <TableCell>
        {progress.trackedBookingId ? (
          <Typography variant="body2">{progress.trackedBookingId}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No recurring booking yet
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Label color={getRewardedLabelColor(row) as LabelColors}>
          {progress.isRewarded ? 'Rewarded' : 'Not rewarded'}
        </Label>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {formatDate(row.created_at ?? row.createdAt)}
      </TableCell>
    </TableRow>
  );
}
